import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';

const AddLedger = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
    partyId,
    isLoading,
}) => {
    const [formData, setFormData] = useState({
        transactionDate: '',
        description: '',
        credit: 0,
        debit: 0,
        voucherType: 'receipt',
        paymentMode: 'cash',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    transactionDate: initialData.transactionDate
                        ? new Date(initialData.transactionDate).toISOString().split('T')[0]
                        : '',
                    description: initialData.description || '',
                    credit: initialData.credit || 0,
                    debit: initialData.debit || 0,
                    voucherType: initialData.voucherType || 'receipt',
                    paymentMode: initialData.paymentMode || 'cash',
                });
            } else {
                setFormData({
                    transactionDate: new Date().toISOString().split('T')[0],
                    description: '',
                    credit: 0,
                    debit: 0,
                    voucherType: 'receipt',
                    paymentMode: 'cash',
                });
            }
            setErrors({});
        }
    }, [isOpen, initialData]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.transactionDate) {
            newErrors.transactionDate = 'Transaction date is required.';
        }
        if (!formData.voucherType) {
            newErrors.voucherType = 'Voucher type is required.';
        }
        if (!formData.paymentMode && formData.voucherType === 'receipt') {
            newErrors.paymentMode = 'Payment mode is required for receipts.';
        }
        if (Number(formData.credit) < 0) {
            newErrors.credit = 'Credit cannot be negative.';
        }
        if (Number(formData.debit) < 0) {
            newErrors.debit = 'Debit cannot be negative.';
        }
        if (Number(formData.credit) === 0 && Number(formData.debit) === 0) {
            newErrors.amount = 'Either Credit or Debit must be greater than 0.';
        }
        if (Number(formData.credit) > 0 && Number(formData.debit) > 0) {
            newErrors.amount = 'Cannot enter both Credit and Debit for a single entry.';
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'number' ? parseFloat(value || null) : value,
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
        if ((name === 'credit' || name === 'debit') && errors.amount) {
            setErrors(prev => ({ ...prev, amount: null }));
        }
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {

            const dataToSend = {
                ...formData,
                transactionDate: formData.transactionDate,
                partyId: partyId,
                credit: Number(formData.credit) || 0,
                debit: Number(formData.debit) || 0,
            };
            onSubmit(dataToSend, initialData?._id);
        }
    };

    if (!isOpen) return null;

    const isEditing = !!initialData;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 transition-opacity duration-300 ease-out">
<div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 shadow-xl">                <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isEditing ? 'Edit Ledger Entry' : 'Add Ledger Entry'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Close modal"
                    >
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Transaction Date */}
                    <div>
                        <label htmlFor="transactionDate" className="block text-sm font-medium text-gray-700 mb-1">
                            Transaction Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="transactionDate"
                            name="transactionDate"
                            value={formData.transactionDate}
                            onChange={handleChange}
                            required
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${errors.transactionDate ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                        />
                        {errors.transactionDate && <p className="text-xs text-red-500 mt-1">{errors.transactionDate}</p>}
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <input
                            type="text"
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Credit & Debit */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="credit" className="block text-sm font-medium text-gray-700 mb-1">
                                Credit (-)
                            </label>
                            <input
                                type="number"
                                id="credit"
                                name="credit"
                                value={formData.credit}
                                onChange={handleChange}
                                min="0"
                                disabled={formData.debit > 0} // Disable if debit has value
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${errors.credit || errors.amount ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} ${formData.debit > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.credit && <p className="text-xs text-red-500 mt-1">{errors.credit}</p>}
                        </div>
                        <div>
                            <label htmlFor="debit" className="block text-sm font-medium text-gray-700 mb-1">
                                Debit (+)
                            </label>
                            <input
                                type="number"
                                id="debit"
                                name="debit"
                                value={formData.debit}
                                onChange={handleChange}
                                min="0"
                                disabled={formData.credit > 0}
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${errors.debit || errors.amount ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} ${formData.credit > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            {errors.debit && <p className="text-xs text-red-500 mt-1">{errors.debit}</p>}
                        </div>
                        {errors.amount && <p className="text-xs text-red-500 mt-1 md:col-span-2">{errors.amount}</p>}
                    </div>


                    {/* Voucher Type */}
                    <div>
                        <label htmlFor="voucherType" className="block text-sm font-medium text-gray-700 mb-1">
                            Voucher Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="voucherType"
                            name="voucherType"
                            value={formData.voucherType}
                            onChange={handleChange}
                            required
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${errors.voucherType ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                        >
                            <option value="sales">Sales</option>
                            <option value="receipt">Receipt</option>

                        </select>
                        {errors.voucherType && <p className="text-xs text-red-500 mt-1">{errors.voucherType}</p>}
                    </div>

                    {/* Payment Mode (Conditionally required maybe) */}
                    <div>
                        <label htmlFor="paymentMode" className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Mode {formData.voucherType === 'receipt' && <span className="text-red-500">*</span>}
                        </label>
                        <select
                            id="paymentMode"
                            name="paymentMode"
                            value={formData.paymentMode}
                            onChange={handleChange}
                            // required={formData.voucherType === 'receipt'}
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${errors.paymentMode ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                        >
                            <option value="cash">Cash</option>
                            <option value="cheque">Cheque</option>
                            <option value="bank transfer">Bank Transfer</option>
                            <option value="other">Other</option>
                        </select>
                        {errors.paymentMode && <p className="text-xs text-red-500 mt-1">{errors.paymentMode}</p>}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
                            {isEditing ? 'Update Entry' : 'Add Entry'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddLedger;